/**
 * Wörter des Verlaufs: Auslöser, Ergebnis, die sieben Zustellstatus, Art und Stand gültiger Werte
 * und die Rückmeldungen je `pricing_publication_*`-Code. Ein unbekannter Code bleibt sichtbar.
 */
export type Tone = 'green' | 'amber' | 'red' | 'grey';

export const TRIGGER: Record<string, string> = {
  BookingNew: 'Neue Buchung',
  BookingModify: 'Buchungsänderung',
  BookingCancel: 'Stornierung',
  BookingPaymentConfirmed: 'Zahlung bestätigt',
  Scheduled: 'Zeitplan',
  ConfigurationChanged: 'Einstellungen geändert',
};

export const OUTCOME: Record<string, { label: string; tone: Tone }> = {
  Change: { label: 'Änderung', tone: 'green' },
  Skip: { label: 'Übersprungen', tone: 'grey' },
  Unchanged: { label: 'Unverändert', tone: 'grey' },
  Target: { label: 'Ziel', tone: 'green' },
  Locked: { label: 'Gesperrt', tone: 'amber' },
  Blocked: { label: 'Blockiert', tone: 'red' },
};

/** Rot nur für Fehlgeschlagen und Bestätigung fehlgeschlagen; Eingereiht ist grau. */
export const PUBLICATION_STATUS: Record<string, { label: string; tone: Tone }> = {
  Queued: { label: 'Eingereiht', tone: 'grey' },
  SendStarted: { label: 'Versand gestartet', tone: 'amber' },
  Sent: { label: 'Gesendet · Bestätigung offen', tone: 'amber' },
  Confirmed: { label: 'Durch Channex bestätigt', tone: 'green' },
  Failed: { label: 'Fehlgeschlagen', tone: 'red' },
  ConfirmationFailed: { label: 'Bestätigung fehlgeschlagen', tone: 'red' },
  Superseded: { label: 'Ersetzt', tone: 'grey' },
};

/** Kurzform im Filter, wo der Zusatz „Bestätigung offen“ nichts sagt. */
export const PUBLICATION_FILTER_LABEL: Record<string, string> = {
  ...Object.fromEntries(
    Object.entries(PUBLICATION_STATUS).map(([status, { label }]) => [status, label]),
  ),
  Sent: 'Gesendet',
  Confirmed: 'Bestätigt',
};

export const EXPOSURE_KIND: Record<string, string> = {
  Base: 'Basis',
  ChannelModifier: 'Kanalaufschlag',
};

export const OFFERING: Record<string, { label: string; tone: Tone }> = {
  Offered: { label: 'Frei', tone: 'green' },
  Booked: { label: 'Gebucht', tone: 'grey' },
  ManualBlock: { label: 'Manuell gesperrt', tone: 'amber' },
  StopSell: { label: 'Verkaufsstopp', tone: 'amber' },
  Pending: { label: 'Vorgemerkt', tone: 'amber' },
  Unknown: { label: 'Unbekannt', tone: 'grey' },
};

export const END_REASON: Record<string, string> = {
  replaced: 'neuer Wert',
  ...Object.fromEntries(Object.entries(OFFERING).map(([key, value]) => [key, value.label])),
};

const PUBLICATION_ERROR: Record<string, string> = {
  pricing_publication_availability_unconfirmed:
    'Die Verfügbarkeit war nicht bestätigt – es wurde nichts gesendet.',
  pricing_publication_booking_conflict: 'Inzwischen gebucht – der Wert wurde nicht gesendet.',
  pricing_publication_claim_lost: 'Eine andere Übertragung hat übernommen.',
  pricing_publication_configuration_ineligible:
    'Die Einstellungen lassen für diesen Tag keine Übertragung zu.',
  pricing_publication_configuration_stale:
    'Die Einstellungen haben sich geändert – der Wert war veraltet.',
  pricing_publication_controls_unavailable: 'Die Publikationssteuerung war nicht verfügbar.',
  pricing_publication_desired_state_stale: 'Ein neuerer Wert war schon berechnet.',
  pricing_publication_generation_superseded: 'Durch eine neuere Übertragung ersetzt.',
  pricing_publication_killed: 'Die Preisautomatik war gestoppt.',
  pricing_publication_manual_ownership: 'Der Wert ist von Hand gesetzt – die Automatik lässt ihn.',
  pricing_publication_max_stay_conflict:
    'Höchstaufenthalt kleiner als der Mindestaufenthalt – nicht gesendet.',
  pricing_publication_property_unavailable: 'Das Apartment war für den Versand nicht verfügbar.',
  pricing_publication_rate_limited: 'Channex hat gebremst – der Versand wird später wiederholt.',
  pricing_publication_readback_drift: 'Channex zeigt einen anderen Wert als gesendet.',
  pricing_publication_readback_missing: 'Channex hat den Wert nicht zurückgemeldet.',
  pricing_publication_readback_rate_limited:
    'Channex hat die Rückfrage gebremst – sie wird später wiederholt.',
  pricing_publication_send_failed: 'Channex hat die Übertragung nicht angenommen.',
};

/** Satz zu einer Rückmeldung; unbekannt: allgemeiner Satz, der Code steht daneben. */
export const publicationErrorText = (code: string) =>
  PUBLICATION_ERROR[code] ?? 'Die Übertragung braucht Aufmerksamkeit.';

export const label = (table: Record<string, string>, key: string) => table[key] ?? key;
